import { CRSManager } from './crs-manager'

function readFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error || new Error('Unable to read file'))
    reader.readAsText(file)
  })
}

function readBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error || new Error('Unable to read binary file'))
    reader.readAsArrayBuffer(file)
  })
}

function featureCollection(features) {
  return { type: 'FeatureCollection', features }
}

function parseCsv(text) {
  const rows = []
  let field = ''
  let row = []
  let quoted = false
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]
    if (char === '"') {
      if (quoted && text[i + 1] === '"') { field += '"'; i += 1 } else quoted = !quoted
    } else if (char === ',' && !quoted) { row.push(field); field = ''
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && text[i + 1] === '\n') i += 1
      row.push(field)
      if (row.some(value => value !== '')) rows.push(row)
      field = ''; row = []
    } else field += char
  }
  if (field || row.length) { row.push(field); rows.push(row) }
  const headers = (rows.shift() || []).map(value => value.trim())
  return { headers, rows: rows.map(values => headers.reduce((record, header, index) => ({ ...record, [header]: coerce(values[index]) }), {})) }
}

function coerce(value) {
  const trimmed = (value || '').trim()
  if (!trimmed) return null
  if (/^(true|false)$/i.test(trimmed)) return trimmed.toLowerCase() === 'true'
  const number = Number(trimmed)
  return Number.isFinite(number) && trimmed !== '' ? number : trimmed
}

function detectCoordinateFields(headers) {
  const fieldFor = candidates => headers.find(header => candidates.includes(header.trim().toLowerCase()))
  return {
    xField: fieldFor(['longitude', 'lon', 'lng', 'x', '经度']),
    yField: fieldFor(['latitude', 'lat', 'y', '纬度'])
  }
}

function csvDataset(parsed, config, file) {
  const xField = config.xField
  const yField = config.yField
  const invalidCount = parsed.rows.filter(row => !Number.isFinite(Number(row[xField])) || !Number.isFinite(Number(row[yField]))).length
  if (invalidCount === parsed.rows.length) throw new Error(`The selected ${xField} and ${yField} fields contain no valid coordinates.`)
  const features = parsed.rows
    .filter(row => Number.isFinite(Number(row[xField])) && Number.isFinite(Number(row[yField])))
    .map((row, index) => ({
      type: 'Feature',
      id: `csv-${index + 1}`,
      geometry: { type: 'Point', coordinates: [Number(row[xField]), Number(row[yField])] },
      properties: row
    }))
  return {
    name: file.name.replace(/\.[^.]+$/, ''), fileName: file.name, fileSize: file.size, format: 'CSV',
    crs: CRSManager.normalize(config.crs || 'EPSG:4326'), data: featureCollection(features),
    metadata: { skippedRows: invalidCount, coordinateFields: { xField, yField } }
  }
}

function textFrom(node, selector) {
  const item = node.querySelector(selector)
  return item ? item.textContent.trim() : ''
}

function parseCoordinateText(value) {
  return value.trim().split(/\s+/).filter(Boolean).map(pair => pair.split(',').slice(0, 2).map(Number)).filter(pair => pair.every(Number.isFinite))
}

function parseKml(text, file) {
  const xml = new DOMParser().parseFromString(text, 'application/xml')
  if (xml.querySelector('parsererror')) throw new Error('The KML file is not valid XML.')
  const features = [...xml.querySelectorAll('Placemark')].map((placemark, index) => {
    const properties = { name: textFrom(placemark, 'name') || `Feature ${index + 1}`, description: textFrom(placemark, 'description') || null }
    const point = placemark.querySelector('Point coordinates')
    const line = placemark.querySelector('LineString coordinates')
    const polygon = placemark.querySelector('Polygon outerBoundaryIs LinearRing coordinates')
    const coordinates = parseCoordinateText((point || line || polygon || {}).textContent || '')
    if (!coordinates.length) return null
    const geometry = point ? { type: 'Point', coordinates: coordinates[0] }
      : line ? { type: 'LineString', coordinates }
        : { type: 'Polygon', coordinates: [coordinates] }
    return { type: 'Feature', id: `kml-${index + 1}`, properties, geometry }
  }).filter(Boolean)
  return { name: file.name.replace(/\.[^.]+$/, ''), fileName: file.name, fileSize: file.size, format: 'KML', crs: 'EPSG:4326', data: featureCollection(features) }
}

function parseGpx(text, file) {
  const xml = new DOMParser().parseFromString(text, 'application/xml')
  if (xml.querySelector('parsererror')) throw new Error('The GPX file is not valid XML.')
  const features = []
  ;[...xml.querySelectorAll('wpt')].forEach((node, index) => {
    features.push({ type: 'Feature', id: `wpt-${index + 1}`, geometry: { type: 'Point', coordinates: [Number(node.getAttribute('lon')), Number(node.getAttribute('lat'))] }, properties: { name: textFrom(node, 'name') || `Waypoint ${index + 1}`, type: 'waypoint' } })
  })
  ;[...xml.querySelectorAll('trk')].forEach((track, index) => {
    const coordinates = [...track.querySelectorAll('trkpt')].map(node => [Number(node.getAttribute('lon')), Number(node.getAttribute('lat'))]).filter(pair => pair.every(Number.isFinite))
    if (coordinates.length > 1) features.push({ type: 'Feature', id: `trk-${index + 1}`, geometry: { type: 'LineString', coordinates }, properties: { name: textFrom(track, 'name') || `Track ${index + 1}`, type: 'track' } })
  })
  ;[...xml.querySelectorAll('rte')].forEach((route, index) => {
    const coordinates = [...route.querySelectorAll('rtept')].map(node => [Number(node.getAttribute('lon')), Number(node.getAttribute('lat'))]).filter(pair => pair.every(Number.isFinite))
    if (coordinates.length > 1) features.push({ type: 'Feature', id: `rte-${index + 1}`, geometry: { type: 'LineString', coordinates }, properties: { name: textFrom(route, 'name') || `Route ${index + 1}`, type: 'route' } })
  })
  return { name: file.name.replace(/\.[^.]+$/, ''), fileName: file.name, fileSize: file.size, format: 'GPX', crs: 'EPSG:4326', data: featureCollection(features) }
}

const GeoJSONImporter = {
  id: 'geojson', extensions: ['geojson', 'json'],
  async import(file) {
    let parsed
    try { parsed = JSON.parse(await readFile(file)) } catch (error) { throw new Error(`Could not parse GeoJSON: ${error.message}`) }
    const data = parsed.type === 'FeatureCollection' ? parsed : parsed.type === 'Feature' ? featureCollection([parsed]) : null
    if (!data) throw new Error('The file must contain a GeoJSON Feature or FeatureCollection.')
    const crs = CRSManager.normalize(parsed.crs && parsed.crs.properties && parsed.crs.properties.name)
    if (!CRSManager.supported.includes(crs)) throw new Error(`CRS ${crs} is not available in this phase. Reproject to EPSG:4326 or EPSG:3857 before import.`)
    return { name: file.name.replace(/\.[^.]+$/, ''), fileName: file.name, fileSize: file.size, format: 'GeoJSON', crs, data }
  }
}

const CSVImporter = {
  id: 'csv', extensions: ['csv'],
  async import(file) {
    const parsed = parseCsv(await readFile(file))
    const detected = detectCoordinateFields(parsed.headers)
    if (!detected.xField || !detected.yField) return { type: 'configuration-required', importerId: 'csv', file, parsed, columns: parsed.headers, detected }
    return csvDataset(parsed, { ...detected, crs: 'EPSG:4326' }, file)
  },
  finalize(file, parsed, config) { return csvDataset(parsed, config, file) }
}

const KMLImporter = { id: 'kml', extensions: ['kml'], async import(file) { return parseKml(await readFile(file), file) } }
const GPXImporter = { id: 'gpx', extensions: ['gpx'], async import(file) { return parseGpx(await readFile(file), file) } }
const ShapefileImporter = {
  id: 'shapefile', extensions: ['zip', 'shp'],
  async import(file) {
    if (file.name.toLowerCase().endsWith('.shp')) throw new Error('Shapefile import requires a .zip archive containing matching .shp, .shx and .dbf files.')
    return { type: 'worker-import', operation: 'shapefile', fileName: file.name, fileSize: file.size, name: file.name.replace(/\.[^.]+$/, ''), buffer: await readBuffer(file) }
  }
}
const FlatGeobufImporter = {
  id: 'flatgeobuf', extensions: ['fgb'],
  async import(file) { return { type: 'worker-import', operation: 'flatgeobuf', fileName: file.name, fileSize: file.size, name: file.name.replace(/\.[^.]+$/, ''), buffer: await readBuffer(file) } }
}
const GeoPackageImporter = {
  id: 'geopackage', extensions: ['gpkg'],
  async import(file) { return { type: 'worker-import', operation: 'geopackage', fileName: file.name, fileSize: file.size, name: file.name.replace(/\.[^.]+$/, ''), buffer: await readBuffer(file) } }
}
const GeoTIFFImporter = {
  id: 'geotiff', extensions: ['tif', 'tiff'],
  async import(file) { return { type: 'worker-import', operation: 'geotiff', fileName: file.name, fileSize: file.size, name: file.name.replace(/\.[^.]+$/, ''), buffer: await readBuffer(file) } }
}

export class DataImporterRegistry {
  constructor() { this.importers = [] }
  register(importer) { this.importers.push(importer) }
  getByFile(file) {
    const extension = (file.name.split('.').pop() || '').toLowerCase()
    return this.importers.find(importer => importer.extensions.includes(extension))
  }
  async import(file) {
    const importer = this.getByFile(file)
    if (!importer) throw new Error(`Unsupported format: .${file.name.split('.').pop()}. Supported formats include GeoJSON, CSV, KML, GPX, Shapefile ZIP, FlatGeobuf, GeoPackage and GeoTIFF.`)
    return importer.import(file)
  }
  get(id) { return this.importers.find(importer => importer.id === id) }
}

export const dataImporters = new DataImporterRegistry()
;[GeoJSONImporter, CSVImporter, KMLImporter, GPXImporter, ShapefileImporter, FlatGeobufImporter, GeoPackageImporter, GeoTIFFImporter].forEach(importer => dataImporters.register(importer))
